import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import i18next from 'i18next';
import { NavigateFunction, useNavigate } from 'react-router';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { useExtendedAuth } from '../../util/auth0';
import SecondaryButton from '../SecondaryButton';
import PrimaryButton from '../PrimaryButton';
import LoadingSpinner from '../LoadingSpinner';
import ConfirmationModal from '../ConfirmationModal';
import { PatientListDto } from '../../../../shared/dtos/patient.dto';
import { TherapistInfoDto } from '../../../../shared/dtos/therapist.dto';
import { TelerehabilitationProgramDto } from '../../../../shared/dtos/telerehabilitationProgram.dto';
import PatientsTable, { ChangeTherapist, PatientBilling, PatientUpload } from './PatientsTable';
import CreatePatientModal from './CreatePatientModal';
import PatientEditModal from './PatientEditModal';
import PatientFileUploadModal from './PatientFileUploadModal';
import PatientCompletionConversationModal from './PatientCompletionConversationModal';
import PatientCompleteModal from './PatientCompleteModal';
import PatientBillingModal from './PatientBillingModal';
import ChangeTherapistModal from './ChangeTherapistModal';
import PatientDeleteModal from './PatientDeleteModal';
import PatientCancelModal from './PatientCancelModal';
import PatientExportModal from './PatientExportModal';

type PatientsOverviewProps = {
  variant: 'therapist' | 'admin';
};

const PatientsOverview: FC<PatientsOverviewProps> = ({ variant }: PatientsOverviewProps): ReactElement => {
  const isAdmin: boolean = variant === 'admin';

  const navigate: NavigateFunction = useNavigate();
  const { authFetch } = useAuthFetchFromRestAPI();
  const { isSuperAdminUser } = useExtendedAuth();

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isLoadingTherapists, setIsLoadingTherapists] = useState(false);
  const [isLoadingTelerehabilitationPrograms, setIsLoadingTelerehabilitationPrograms] = useState(false);

  const [showCreatePatientModal, setShowCreatePatientModal] = useState(false);
  const [editPatient, setEditPatient] = useState<PatientListDto | null>(null);
  const [patientUploadData, setPatientUploadData] = useState<PatientUpload | null>(null);
  const [patientCompleteConversation, setPatientCompleteConversation] = useState<string | null>(null);
  const [completePatientId, setCompletePatientId] = useState<string | null>(null);
  const [patientBillingData, setPatientBillingData] = useState<PatientBilling | null>(null);
  const [deletePatientId, setDeletePatientId] = useState<string | null>(null);
  const [cancelPatientId, setCancelPatientId] = useState<string | null>(null);
  const [exportPatientId, setExportPatientId] = useState<string | null>(null);
  const [changeTherapist, setChangeTherapist] = useState<ChangeTherapist | null>(null);
  const [showMissingInfoModal, setShowMissingInfoModal] = useState<PatientListDto | null>(null);

  const [rows, setRows] = useState<PatientListDto[]>([]);
  const [therapists, setTherapists] = useState<TherapistInfoDto[]>([]);
  const [telerehabilitationPrograms, setTelerehabilitationPrograms] = useState<TelerehabilitationProgramDto[]>([]);

  const isLoading: boolean = isLoadingPatients || isLoadingTelerehabilitationPrograms || (isAdmin && isLoadingTherapists);

  const fetchTherapistInfos: () => Promise<void> = useCallback(async () => {
    setIsLoadingTherapists(true);

    try {
      const result: TherapistInfoDto[] = await authFetch('/api/v1/admin/therapist-infos');
      setTherapists(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingTherapists(false);
    }
  }, [authFetch]);

  const fetchTelerehabilitationPrograms: () => Promise<void> = useCallback(async () => {
    setIsLoadingTelerehabilitationPrograms(true);

    try {
      const result: TelerehabilitationProgramDto[] = await authFetch('/api/v1/telerehabilitation-program');
      setTelerehabilitationPrograms(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingTelerehabilitationPrograms(false);
    }
  }, [authFetch]);

  const fetchPatients: () => Promise<void> = useCallback(async () => {
    setIsLoadingPatients(true);

    try {
      const endpoint: string = isAdmin ? '/api/v1/admin/patients' : '/api/v1/patients/list';
      const result: PatientListDto[] = await authFetch(endpoint);
      setRows(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingPatients(false);
    }
  }, [authFetch, isAdmin]);

  const handlePatientEdited: (patientId: string, savedSocialSecurityNumber?: string | null) => Promise<void> = useCallback(
    async (patientId: string, savedSocialSecurityNumber?: string | null) => {
      await fetchPatients();

      if (!savedSocialSecurityNumber) {
        return;
      }

      setRows((prev: PatientListDto[]) =>
        prev.map((row: PatientListDto) =>
          row.id === patientId
            ? { ...row, patientInfo: { ...row.patientInfo, ssn: savedSocialSecurityNumber } }
            : row
        )
      );
    },
    [fetchPatients]
  );

  useEffect(() => {
    void fetchPatients();
    void fetchTelerehabilitationPrograms();

    if (isAdmin) {
      void fetchTherapistInfos();
    }
  }, [fetchPatients, fetchTelerehabilitationPrograms, fetchTherapistInfos, isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    (async () => setIsSuperAdmin(await isSuperAdminUser()))();
  }, [isAdmin, isSuperAdminUser]);

  const createInstantRoom: () => Promise<void> = async (): Promise<void> => {
    try {
      const roomCode: string = await authFetch('/api/v1/room/instant-room', {
        method: 'POST'
      });

      if (roomCode) {
        navigate(`/call/${roomCode}`);
      }
    } catch (_) {}
  };

  const onUploadFile = (data: PatientUpload): void => {
    if (!data.isCompletion) {
      setPatientUploadData(data);
      return;
    }

    const isAllowed: boolean = !!data.patient.patientInfo.ssn &&
      !!data.patient.patientInfo.teleRehaAftercareStart &&
      !!data.patient.patientInfo.teleRehaAftercareEnd;

    if (isAllowed) {
      setPatientUploadData(data);
    } else {
      setShowMissingInfoModal(data.patient);
    }
  };

  return (
    <div className="bg-white p-4">
      {!isAdmin && (
        <Row className="align-items-center mb-4">
          <Col><h1 className="m-0">{i18next.t('patientsPage.title')}</h1></Col>
        </Row>
      )}

      {isLoading && (
        <LoadingSpinner />
      )}

      {!isLoading && (
        <PatientsTable
          page={isAdmin ? 'admin' : 'default'}
          rows={rows}
          showAdditionalInfo={isAdmin}
          toolbarRight={!isAdmin ? (
            <div className="d-flex gap-2">
              <SecondaryButton onClick={createInstantRoom}>
                {i18next.t('patientsPage.createRoom')}
              </SecondaryButton>
              <PrimaryButton onClick={() => setShowCreatePatientModal(true)}>
                {i18next.t('patientsPage.createPatient')}
              </PrimaryButton>
            </div>
          ) : undefined}
          onNavigate={!isAdmin ? (therapyId: string) => navigate(`/therapien/${therapyId}`) : undefined}
          onPatientEdit={(row: PatientListDto) => setEditPatient(row)}
          onUploadFile={onUploadFile}
          onSetCompletionConversation={(patientId: string) => setPatientCompleteConversation(patientId)}
          onCompletePatient={(patientId: string) => setCompletePatientId(patientId)}
          onExportFile={(patientId: string) => setExportPatientId(patientId)}
          onCancelPatient={(patientId: string): void => setCancelPatientId(patientId)}
          onBillingPatient={isAdmin ? (data: PatientBilling) => setPatientBillingData(data) : undefined}
          onDeletePatient={isAdmin && isSuperAdmin ? (patientId: string): void => setDeletePatientId(patientId) : undefined}
          onTherapistChange={isAdmin ? (data: ChangeTherapist) => setChangeTherapist(data) : undefined}
        />
      )}

      {editPatient && (
        <PatientEditModal
          show={!!editPatient}
          patient={editPatient}
          telerehabilitationPrograms={telerehabilitationPrograms}
          onClose={() => setEditPatient(null)}
          onSave={(savedSocialSecurityNumber?: string | null) => {
            const patientId: string = editPatient.id;
            setEditPatient(null);
            void handlePatientEdited(patientId, savedSocialSecurityNumber);
          }}
        />
      )}

      {patientUploadData && (
        <PatientFileUploadModal
          show={!!patientUploadData}
          patientId={patientUploadData.patient.id}
          isCompletion={patientUploadData.isCompletion}
          onClose={() => setPatientUploadData(null)}
          onSave={() => {
            void fetchPatients();
            setPatientUploadData(null);
          }}
        />
      )}

      {patientCompleteConversation && (
        <PatientCompletionConversationModal
          show={!!patientCompleteConversation}
          patientId={patientCompleteConversation}
          onClose={() => setPatientCompleteConversation(null)}
          onSave={() => {
            void fetchPatients();
            setPatientCompleteConversation(null);
          }}
        />
      )}

      {completePatientId && (
        <PatientCompleteModal
          show={!!completePatientId}
          patientId={completePatientId}
          onClose={() => setCompletePatientId(null)}
          onSave={() => {
            void fetchPatients();
            setCompletePatientId(null);
          }}
        />
      )}

      {cancelPatientId && (
        <PatientCancelModal
          show={!!cancelPatientId}
          patientId={cancelPatientId}
          onClose={() => setCancelPatientId(null)}
          onSave={() => {
            void fetchPatients();
            setCancelPatientId(null);
          }} />
      )}

      {exportPatientId && (
        <PatientExportModal
          show={!!exportPatientId}
          patientId={exportPatientId}
          onClose={() => setExportPatientId(null)}
        />
      )}

      {isAdmin && patientBillingData && (
        <PatientBillingModal
          show={!!patientBillingData}
          patientId={patientBillingData.patientId}
          billingInfo={patientBillingData.billingInfo}
          onClose={() => setPatientBillingData(null)}
          onSave={() => {
            void fetchPatients();
            setPatientBillingData(null);
          }}
        />
      )}

      {isAdmin && changeTherapist && (
        <ChangeTherapistModal
          show={!!changeTherapist}
          therapists={therapists}
          data={changeTherapist}
          onClose={() => setChangeTherapist(null)}
          onSave={() => {
            void fetchPatients();
            setChangeTherapist(null);
          }}
        />
      )}

      {isAdmin && deletePatientId && (
        <PatientDeleteModal
          show={!!deletePatientId}
          patientId={deletePatientId}
          onClose={() => setDeletePatientId(null)}
          onSave={() => {
            void fetchPatients();
            setDeletePatientId(null);
          }} />
      )}

      {!isAdmin && (
        <CreatePatientModal
          show={showCreatePatientModal}
          telerehabilitationPrograms={telerehabilitationPrograms}
          onClose={() => setShowCreatePatientModal(false)}
          onSave={() => {
            void fetchPatients();
            setShowCreatePatientModal(false);
          }}
        />
      )}

      <ConfirmationModal
        show={!!showMissingInfoModal}
        title={i18next.t('patientsTable.missingModal.title')}
        body={i18next.t('patientsTable.missingModal.body')}
        buttonPositive={i18next.t('patientsTable.missingModal.buttonPositive')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={() => {
          setShowMissingInfoModal(null);
          setEditPatient(showMissingInfoModal);
        }}
        onNegative={() => setShowMissingInfoModal(null)}
      />
    </div>
  );
};

export default PatientsOverview;
